import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Music, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getMediaShare, acceptMediaShareInvite, recordMediaView, type MediaShareView } from '../services/mediaShareService';

/**
 * Invite-share landing (wisetree.ai/join-media/{mediaId}). The shared asset is fully viewable; the
 * gate is on JOINING — signed-out visitors see the content + a "Sign in to join" CTA (the
 * soft-paywall conversion moment is the action, not the content). After sign-in we accept the
 * invite and drop them into the chat.
 */
const JoinMediaPage: React.FC = () => {
  const { mediaId } = useParams<{ mediaId: string }>();
  const navigate = useNavigate();
  const { user, signInWithGoogle } = useAuth();
  const [share, setShare] = useState<MediaShareView | null>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!mediaId) return;
      try { const s = await getMediaShare(mediaId); if (alive) setShare(s); }
      catch (e: any) { if (alive) setError(e?.message || 'This invite is not available.'); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [mediaId]);

  // Count a view once the shared asset is actually on screen (3s dwell, like the /m/{id} viewer) — this landing
  // is a real viewing surface, so it should feed the media-analytics/virality stats, not just the /m page.
  useEffect(() => {
    if (!mediaId || loading || error || !share) return;
    const t = setTimeout(() => { recordMediaView(mediaId); }, 3000);
    return () => clearTimeout(t);
  }, [mediaId, loading, error, share]);

  const join = async () => {
    if (!mediaId || joining) return;
    setJoining(true); setError('');
    try {
      if (!user) await signInWithGoogle();
      const { slug } = await acceptMediaShareInvite(mediaId);
      navigate(`/group/${slug}`, { replace: true });
    } catch (e: any) {
      setError(e?.message || 'Could not join the chat');
    } finally { setJoining(false); }
  };

  return (
    <div className="min-h-screen bg-secondary-950 text-white flex flex-col">
      <header className="flex items-center justify-between px-5 py-4">
        <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm font-semibold tracking-tight">
          <Sparkles className="w-4 h-4" /> Wisetree
        </button>
      </header>
      <main className="flex-1 flex flex-col items-center justify-center px-4 pb-10 gap-5 text-center">
        {loading && <Loader2 className="w-7 h-7 animate-spin text-secondary-400" />}
        {!loading && error && <p className="text-secondary-300 text-sm">{error}</p>}
        {!loading && share && (
          <>
            {share.kind === 'image' && <img src={share.mediaUrl} alt={share.title} className="max-h-[64dvh] w-auto rounded-2xl object-contain shadow-2xl" />}
            {share.kind === 'video' && <video src={share.mediaUrl} controls autoPlay playsInline className="max-h-[64dvh] w-auto rounded-2xl shadow-2xl bg-black" />}
            {share.kind === 'audio' && (
              <div className="w-full max-w-md rounded-2xl bg-secondary-900 border border-secondary-800 p-8 flex flex-col items-center gap-5">
                <div className="w-20 h-20 rounded-full bg-secondary-800 flex items-center justify-center"><Music className="w-9 h-9 text-secondary-300" /></div>
                <audio src={share.mediaUrl} controls className="w-full" />
              </div>
            )}
            <div>
              <p className="text-sm text-secondary-300">Join this chat to create your own.</p>
              <button onClick={join} disabled={joining}
                className="mt-2 inline-flex items-center gap-2 rounded-full bg-white text-secondary-900 px-5 py-2 text-sm font-medium hover:bg-secondary-100 disabled:opacity-60">
                {joining && <Loader2 className="w-4 h-4 animate-spin" />}
                {user ? 'Accept invite & join' : 'Sign in to join'}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default JoinMediaPage;
